import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CalendarDays, Copy, RefreshCw, Plus, Trash2, Loader2, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

function copyText(text, label) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Could not copy"));
}

const feedUrlFor = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/calendars/feed/${token}.ics`;

function FeedCard({ feedUrl, onRegenerate }) {
  const [showHelp, setShowHelp] = useState(false);
  if (!feedUrl) {
    return (
      <div className="border border-sand-200 p-4 mb-6" data-testid="calendar-feed-card">
        <p className="label-tag mb-1">Your travel calendar feed</p>
        <p className="text-xs text-sand-500 mb-3">
          Generate a private feed URL to subscribe from Google Calendar and see every family trip alongside your work schedule.
        </p>
        <button onClick={onRegenerate} className="btn-swiss btn-primary text-sm" data-testid="feed-generate">
          Generate feed link
        </button>
      </div>
    );
  }
  return (
    <div className="border border-sand-200 p-4 mb-6" data-testid="calendar-feed-card">
      <p className="label-tag mb-1">Your travel calendar feed</p>
      <p className="text-xs text-sand-500 mb-3">
        Subscribe from Google Calendar to see every family trip — flights, hotels, cars and events — alongside your work schedule. Unbooked items show as [PLANNED].
      </p>
      <div className="flex items-center gap-2 border border-dashed border-sand-300 bg-sand-50 px-3 py-2">
        <p className="flex-1 font-mono text-[11px] text-sand-600 truncate" data-testid="feed-url">{feedUrl}</p>
        <button onClick={() => copyText(feedUrl, "Feed URL")} className="shrink-0 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-ember hover:underline" data-testid="feed-copy">
          <Copy size={11} /> Copy
        </button>
      </div>
      <div className="flex items-center justify-between mt-3">
        <button onClick={() => setShowHelp((v) => !v)} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-sand-500 hover:text-ember" data-testid="feed-help-toggle">
          {showHelp ? <ChevronUp size={11} /> : <ChevronDown size={11} />} How to add to Google Calendar
        </button>
        <button
          onClick={() => { if (window.confirm("Regenerate the feed URL? The old URL stops working and you'll need to re-subscribe in Google Calendar.")) onRegenerate(); }}
          className="font-mono text-[10px] uppercase tracking-wide text-sand-400 hover:text-error" data-testid="feed-regenerate">
          Regenerate URL
        </button>
      </div>
      {showHelp && (
        <ol className="mt-3 text-xs text-sand-600 space-y-1.5 list-decimal pl-4" data-testid="feed-help">
          <li>Copy the feed URL above.</li>
          <li>In Google Calendar (desktop): left sidebar → <b>Other calendars</b> → <b>+</b> → <b>From URL</b>.</li>
          <li>Paste the URL and click <b>Add calendar</b>.</li>
          <li className="text-sand-400">Google refreshes subscribed calendars on its own schedule — typically every 8–24 hours.</li>
        </ol>
      )}
    </div>
  );
}

function CalRow({ cal, onSync, onDelete, syncing }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-sand-100" data-testid={`cal-row-${cal.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{cal.label}</p>
        <p className="text-[10px] font-mono text-sand-400 truncate">
          {cal.event_count} events · synced {cal.last_synced_at ? cal.last_synced_at.slice(0, 16).replace("T", " ") : "never"}
        </p>
        {cal.last_error && (
          <p className="text-[10px] font-mono text-error flex items-center gap-1 mt-0.5" data-testid={`cal-error-${cal.id}`}>
            <AlertTriangle size={10} /> {cal.last_error}
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <button onClick={() => onSync(cal)} disabled={syncing === cal.id}
          className="p-1.5 text-sand-400 hover:text-ember disabled:opacity-40" title="Sync now" data-testid={`cal-sync-${cal.id}`}>
          <RefreshCw size={13} className={syncing === cal.id ? "animate-spin" : ""} />
        </button>
        <button onClick={() => onDelete(cal)} className="p-1.5 text-sand-400 hover:text-error" title="Disconnect" data-testid={`cal-delete-${cal.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

export default function CalendarSection() {
  const [data, setData] = useState(null);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(null);

  const load = () => {
    api.get("/calendars").then(({ data }) => setData(data)).catch(() => toast.error("Could not load calendars"));
  };
  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data: res } = await api.post("/calendars", { label: label.trim(), url: url.trim() });
      toast.success(`Connected — ${res.calendar.event_count} events synced`);
      setLabel(""); setUrl("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect that calendar");
    } finally {
      setAdding(false);
    }
  };

  const sync = async (cal) => {
    setSyncing(cal.id);
    try {
      const { data: res } = await api.post(`/calendars/${cal.id}/sync`);
      toast.success(`Synced ${res.event_count} events`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(null);
    }
  };

  const remove = async (cal) => {
    if (!window.confirm(`Disconnect "${cal.label}"? The AI will no longer see these events.`)) return;
    try {
      await api.delete(`/calendars/${cal.id}`);
      toast.success("Calendar disconnected");
      load();
    } catch { toast.error("Could not disconnect"); }
  };

  const regenerate = async () => {
    try {
      const { data: res } = await api.post("/calendars/feed/regenerate");
      setData((d) => ({ ...d, feed_token: res.feed_token }));
      toast.success("New feed URL generated — re-subscribe in Google Calendar");
    } catch { toast.error("Could not regenerate"); }
  };

  if (!data) return <section className="bg-surface rounded-card border border-hairline p-6 md:p-8 mt-6 h-48 animate-pulse" data-testid="calendar-section-loading" />;

  return (
    <section className="bg-surface rounded-card border border-hairline p-6 md:p-8 mt-6" data-testid="calendar-section">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={15} className="text-ember" />
        <h2 className="font-display font-medium text-xl tracking-tight">Calendars</h2>
      </div>
      <p className="text-xs text-sand-500 mb-5 max-w-md">
        Push your travel into Google Calendar, and let the AI read your work/personal calendars to plan around your commitments.
      </p>

      <FeedCard feedUrl={data.feed_token ? feedUrlFor(data.feed_token) : null} onRegenerate={regenerate} />

      <div className="border-t border-sand-100 pt-5">
        <p className="label-tag mb-1">Connected calendars ({data.calendars.length})</p>
        <p className="text-xs text-sand-500 mb-2">
          Paste a calendar's <b>secret iCal address</b>. Google Calendar: Settings → your calendar → Integrate calendar → "Secret address in iCal format". Outlook: Settings → Shared calendars → Publish → ICS link. Refreshed hourly.
        </p>
        {data.calendars.map((c) => <CalRow key={c.id} cal={c} onSync={sync} onDelete={remove} syncing={syncing} />)}
        <form onSubmit={add} className="mt-4 space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input required placeholder="Label (e.g. Work)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
              className="sm:w-36 border border-sand-300 px-3 py-2 text-sm focus:outline-none focus:border-ember" data-testid="cal-add-label" />
            <input required type="url" placeholder="https://calendar.google.com/calendar/ical/…/basic.ics" value={url} onChange={(e) => setUrl(e.target.value)}
              className="flex-1 min-w-0 border border-sand-300 px-3 py-2 text-sm font-mono text-xs focus:outline-none focus:border-ember" data-testid="cal-add-url" />
            <button type="submit" disabled={adding} className="btn-swiss btn-primary text-sm disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="cal-add-submit">
              {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Connect
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
